import React from "react"

import { motion } from "framer-motion"
import { buildMutation, compress, useMutation } from "micro-graphql-react"

import { ArrowDownIcon, ArrowUpIcon } from "@heroicons/react/24/solid"

import classNames from "src/classNames"

import ExpandableImageCollection from "components/ExpandableImageCollection"
import EditableDisplay from "components/Forms/EditableDisplay"
import RemoveButton from "components/RemoveButton"

import lessonResourcesFragment from "./lessonResourcesFragment"

const LessonResource = ({ resource, first, last }) => {
  const { runMutation } = useMutation(
    buildMutation(updateLessonResourceMutation)
  )
  const { runMutation: incrementOrder, running: incrementOrderRunning } =
    useMutation(buildMutation(incrementOrderMutation))
  const { runMutation: decrementOrder, running: decrementOrderRunning } =
    useMutation(buildMutation(decrementOrderMutation))

  const ResourceName = ({ children }) => {
    const { runMutation } = useMutation(
      buildMutation(removeLessonResourceMutation)
    )

    return (
      <div className="flex items-center">
        <RemoveButton onClick={() => runMutation({ id: resource.id })} />
        <strong>{children}</strong>
      </div>
    )
  }

  return (
    <motion.li positionTransition className="mb-3 flex justify-between">
      <div className="ml-3 flex gap-4">
        <div className="w-72">
          <EditableDisplay
            displayValue={resource.name}
            save={({ value }) => runMutation({ id: resource.id, name: value })}
            tag={ResourceName}
          />
        </div>
        <div className="mt-2">
          <button
            className={classNames(
              "cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200",
              (last || incrementOrderRunning) && "cursor-not-allowed"
            )}
            onClick={() => incrementOrder({ id: resource.id })}
            disabled={last || incrementOrderRunning}
          >
            <ArrowDownIcon className="h-6 w-6" />
          </button>
          <button
            className={classNames(
              "cursor-pointer rounded-md border-none bg-inherit pt-2 text-xl hover:bg-gray-200",
              (first || decrementOrderRunning) && "cursor-not-allowed"
            )}
            onClick={() => decrementOrder({ id: resource.id })}
            disabled={first || decrementOrderRunning}
          >
            <ArrowUpIcon className="h-6 w-6" />
          </button>
        </div>
      </div>
      <ExpandableImageCollection images={[resource.file]} />
    </motion.li>
  )
}

const updateLessonResourceMutation = compress`
  mutation($id: ID!, $name: String!) {
    updateLessonResource(id: $id, name: $name) {
      lessonResource {
        id
        name
      }
    }
  }
`

const decrementOrderMutation = compress`
  ${lessonResourcesFragment}
  mutation($id: ID!) {
    decrementLessonResourceOrder(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

const incrementOrderMutation = compress`
  ${lessonResourcesFragment}
  mutation($id: ID!) {
    incrementLessonResourceOrder(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

const removeLessonResourceMutation = compress`
  ${lessonResourcesFragment}
  mutation($id: ID!) {
    removeLessonResource(id: $id) {
      lessonTopic {
        id
        lessonResources {
          ...LessonResourcesFields
        }
      }
    }
  }
`

export default LessonResource
